import { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AIChatMessage from "./AIChatMessage";
import AITypingIndicator from "./AITypingIndicator";
import { AI_ASSISTANT_BLUE, STARTER_ACTIONS } from "./constants";

const STARTER_ICONS = {
  "create-quiz": QuizOutlinedIcon,
  "check-quiz": AssessmentOutlinedIcon,
  "ask-quizzes": HelpOutlineRoundedIcon,
  "make-presentation": SlideshowOutlinedIcon,
};

export default function AIChatMessages({
  messages,
  sending,
  restoring,
  sectionLabel,
  onRetry,
  onStarterSelect,
  suggestions,
  onSuggestionSelect,
  suggestionsDisabled,
  draftSlides,
  onDeckAction,
  workspace,
  workspaceKey,
}) {
  const endRef = useRef(null);
  const containerRef = useRef(null);
  // Follow new messages only while the user is at the bottom, so editing an
  // earlier card is never yanked away.
  const stickRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const lastMessage = messages[messages.length - 1];
  const lastId = lastMessage?.id;

  const suggestionKey = Array.isArray(suggestions) ? suggestions.map((item) => item.id || item.label).join("|") : "";

  // Cards sit under the message that produced them, so later messages flow below
  // instead of piling up above cards pinned to the bottom. New cards (a changed
  // key) attach to the newest message.
  const anchorRef = useRef({ key: null, id: null });
  if (workspaceKey !== anchorRef.current.key) {
    anchorRef.current = { key: workspaceKey, id: lastId || null };
  }
  const anchorId =
    workspace && messages.some((m) => m.id === anchorRef.current.id) ? anchorRef.current.id : null;
  const workspaceBlock = workspace ? <Box sx={{ mt: 0.5, mb: 1 }}>{workspace}</Box> : null;

  const scrollToEnd = (behavior = "smooth") => {
    endRef.current?.scrollIntoView({ behavior, block: "end" });
    stickRef.current = true;
    setShowJump(false);
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
    stickRef.current = nearBottom;
    if (nearBottom) setShowJump(false);
  };

  // Your own new prompt always scrolls into view.
  useLayoutEffect(() => {
    if (lastMessage?.role === "user") stickRef.current = true;
  }, [lastId, lastMessage?.role]);

  useEffect(() => {
    if (stickRef.current) scrollToEnd();
    else if (lastId) setShowJump(true);
  }, [lastId, sending, suggestionKey, workspaceKey]);

  useEffect(() => {
    if (!restoring) scrollToEnd("auto");
  }, [restoring]);

  if (restoring) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} sx={{ color: AI_ASSISTANT_BLUE }} />
      </Box>
    );
  }

  if (!messages.length && !sending) {
    return (
      <Box
        ref={containerRef}
        data-testid="ai-chat-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          px: 2,
          py: 3,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.elevated",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              mb: 1.5,
              mx: "auto",
              background: `linear-gradient(135deg, ${AI_ASSISTANT_BLUE}, #3b82f6)`,
            }}
          >
            <AutoAwesomeRoundedIcon />
          </Avatar>
          <Typography fontWeight={700} sx={{ color: "ink.strong", mb: 0.4 }}>
            Microlab AI Assistant
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            You’re in <strong>{sectionLabel}</strong>. Pick a starting point:
          </Typography>
        </Box>

        <Stack spacing={1}>
          {STARTER_ACTIONS.map((action) => {
            const Icon = STARTER_ICONS[action.id] || AutoAwesomeRoundedIcon;
            return (
              <Box
                key={action.id}
                role="button"
                tabIndex={0}
                onClick={() => onStarterSelect?.(action)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onStarterSelect?.(action);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                  p: 1.4,
                  borderRadius: 2.5,
                  bgcolor: "background.card",
                  border: "1px solid rgba(148,163,184,0.28)",
                  cursor: "pointer",
                  transition: "border-color .15s ease, box-shadow .15s ease",
                  "&:hover": {
                    borderColor: "rgba(0, 97, 254, 0.45)",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: action.comingSoon ? "#f1f5f9" : "rgba(0, 97, 254, 0.1)",
                    color: action.comingSoon ? "#64748b" : AI_ASSISTANT_BLUE,
                  }}
                >
                  <Icon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography fontWeight={700} sx={{ fontSize: 14, color: "ink.strong" }}>
                      {action.label}
                    </Typography>
                    {action.comingSoon ? (
                      <Chip
                        size="small"
                        label="Soon"
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9" }}
                      />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.2 }}>
                    {action.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
        {workspace ? <Box sx={{ mt: 2 }}>{workspace}</Box> : null}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      role="log"
      aria-live="polite"
      aria-label="AI assistant conversation"
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        overscrollBehavior: "contain",
        scrollBehavior: "smooth",
        px: 1.5,
        py: 2,
        bgcolor: "background.elevated",
      }}
    >
      <Stack spacing={0} sx={{ minWidth: 0, maxWidth: "100%" }}>
        {messages.map((message, index) => {
          const isLast = index === messages.length - 1;
          const cards = message.id === anchorId ? workspaceBlock : null;
          if (message.event) {
            return (
              <Box key={message.id}>
                <AIChatMessage
                  message={message}
                  suggestions={isLast && !sending && suggestionKey ? suggestions : null}
                  onSuggestionSelect={onSuggestionSelect}
                  suggestionsDisabled={suggestionsDisabled}
                />
                {cards}
              </Box>
            );
          }
          const showSuggestions =
            isLast && message.role !== "user" && !sending && suggestionKey;
          return (
            <Box key={message.id}>
              <AIChatMessage
                message={message}
                onRetry={onRetry}
                suggestions={showSuggestions ? suggestions : null}
                onSuggestionSelect={onSuggestionSelect}
                suggestionsDisabled={suggestionsDisabled}
                draftSlides={isLast && message.role !== "user" ? draftSlides : null}
                onDeckAction={isLast && message.role !== "user" ? onDeckAction : null}
              />
              {cards}
            </Box>
          );
        })}
        {anchorId ? null : workspaceBlock}
        {sending ? (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
            <AITypingIndicator />
          </Box>
        ) : null}
        <div ref={endRef} />
      </Stack>
    </Box>
    {showJump ? (
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={() => scrollToEnd()}
        startIcon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
        sx={{
          position: "absolute",
          bottom: 10,
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          fontSize: 12,
          color: "#fff !important",
          bgcolor: `${AI_ASSISTANT_BLUE} !important`,
          boxShadow: "0 6px 16px rgba(0,97,254,0.3)",
        }}
      >
        New messages
      </Button>
    ) : null}
    </Box>
  );
}

AIChatMessages.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  sending: PropTypes.bool,
  restoring: PropTypes.bool,
  sectionLabel: PropTypes.string,
  onRetry: PropTypes.func,
  onStarterSelect: PropTypes.func,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  onSuggestionSelect: PropTypes.func,
  suggestionsDisabled: PropTypes.bool,
  draftSlides: PropTypes.arrayOf(PropTypes.object),
  onDeckAction: PropTypes.func,
  workspace: PropTypes.node,
  workspaceKey: PropTypes.string,
};
